import Nav from "../components/Nav";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import { Link } from "react-router-dom";

const WEEKS = [
  { n: "Week 1", h: "Arrival & Assessment", p: "Comprehensive health screening, nervous-system baseline, private intake with a Saviar guide, and the beginning of your daily rhythm — soft mornings, herbal regimen, and gentle nourishment." },
  { n: "Week 2", h: "Deep Reset", p: "You settle into the protocols. Nutrition structure deepens, identity coaching intensifies, and your body begins to release the weight it has been carrying — physically, biochemically, emotionally." },
  { n: "Week 3", h: "Integration & Celebration", p: "You emerge with new frameworks for how you eat, move, decide, and desire. The residency closes with a private sommelier-led wine celebration and your Saviar Society induction." },
];

import useMeta from "../hooks/useMeta";

export default function Experiences() {
  useMeta("Experiences | Saviar",
    "The 21-day Soft Life Reset — a residential wellness experience in the Washington, DC area for women ready to come home to themselves.");
  return (
    <div data-testid="experiences-page">
      <Nav />
      <div className="page-hero">
        <div className="page-hero-bg" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=1600&q=80')" }} />
        <div className="page-hero-content">
          <p className="eyebrow eyebrow-light">The Experience</p>
          <h1>Every gathering,<br/>a composition.</h1>
          <p>From the 21-day residential reset to intimate curated gatherings, Saviar experiences are designed for the woman who demands more from her life.</p>
        </div>
      </div>

      <section className="section-sm" style={{ background: "var(--ivory)" }}>
        <div className="container-x">
          <div className="grid-2" style={{ marginBottom: 80, paddingBottom: 80, borderBottom: "1px solid var(--border-gold)" }}>
            <Reveal>
              <div className="feature-image-wrap">
                <img src="/images/owner-morning-tea.jpg" alt="Saviar founder with morning tea and a journal in bed" />
                <div className="feature-tag">Most Popular</div>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <p className="eyebrow">Signature Program</p>
              <div className="divider" />
              <h2 className="display" style={{ fontSize: "clamp(1.8rem, 3vw, 2.6rem)", color: "var(--charcoal)", marginBottom: 20 }}>The Soft Life Reset</h2>
              <p className="body-lg" style={{ marginBottom: 24 }}>
                A 21-day residential wellness transformation rooted in biochemistry, nervous system regulation, feminine embodiment, and personalized nutrition. Reset your body, refine your habits, raise your identity.
              </p>
              <ul style={{ listStyle: "none", padding: 0, display: "flex", flexDirection: "column", gap: 10, marginBottom: 32 }}>
                {["Daily guided protocols and rituals", "Nervous-system regulation toolkit", "Personalized nutrition framework", "Identity elevation workbook", "Closing sommelier celebration", "Alumni Society induction"].map((x) => (
                  <li key={x} style={{ display: "flex", gap: 12, fontSize: ".9rem", color: "var(--warm-gray)" }}><span style={{ color: "var(--gold)" }}>—</span> {x}</li>
                ))}
              </ul>
              <Link to="/apply" className="btn btn-primary" data-testid="apply-reset">Apply for This Program</Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WEEK BY WEEK */}
      <section className="section dark-section">
        <div className="container-x">
          <div style={{ textAlign: "center", maxWidth: 720, margin: "0 auto 56px" }}>
            <p className="eyebrow" style={{ marginBottom: 16 }}>Week by Week</p>
            <h2 className="display" style={{ fontSize: "clamp(1.8rem, 3vw, 2.8rem)", color: "var(--ivory)" }}>Twenty-one days, three movements.</h2>
          </div>
          <div className="grid-3" data-testid="weeks-grid">
            {WEEKS.map((w, i) => (
              <Reveal key={w.n} delay={i * 120}>
                <div style={{ padding: 40, border: "1px solid rgba(184,150,90,.15)", height: "100%" }}>
                  <p className="eyebrow" style={{ marginBottom: 16 }}>{w.n}</p>
                  <div className="divider" />
                  <h3 style={{ fontFamily: "var(--serif)", fontWeight: 300, fontSize: "1.6rem", color: "var(--ivory)", marginBottom: 16 }}>{w.h}</h3>
                  <p className="body-sm">{w.p}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="section" style={{ background: "var(--ivory)" }}>
        <div className="container-x">
          <div className="grid-2">
            <Reveal>
              <p className="eyebrow">What's Included</p>
              <div className="divider" />
              <h2 className="display" style={{ fontSize: "clamp(1.8rem, 3vw, 2.6rem)", color: "var(--charcoal)", marginBottom: 24 }}>
                A complete residency, curated to the detail.
              </h2>
              <p className="body-lg">Every Saviar residency includes your own bed in a shared or private room within a private Washington, DC area residence, chef-prepared meals three times daily, daily programming, and attentive guest care.</p>
            </Reveal>
            <Reveal delay={120}>
              {[
                "Your own bed in a shared or private room in a private Washington, DC area residence",
                "Comprehensive intake + health screening",
                "Personalized herbal + supplement regimen",
                "Chef-prepared meals, three daily",
                "Daily identity coaching + journaling",
                "Movement, breathwork, restorative rituals",
                "Closing sommelier wine celebration",
                "Saviar Society alumni induction",
              ].map((x, i) => (
                <div key={x} className="benefit-item on-light">
                  <div className="benefit-num">{String(i + 1).padStart(2, "0")}</div>
                  <div><h4>{x}</h4></div>
                </div>
              ))}
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="section" style={{ background: "var(--champagne)" }}>
        <div className="container-x" style={{ maxWidth: 820 }}>
          <Reveal>
            <p className="eyebrow" style={{ marginBottom: 16 }}>Who It's For</p>
            <h2 className="display" style={{ fontSize: "clamp(1.8rem, 3vw, 2.6rem)", color: "var(--charcoal)", marginBottom: 24 }}>
              For the woman who has done the work — and is ready to soften.
            </h2>
            <p className="body-lg">Saviar is for high-performing women in a season of transition: recovering from burnout, redefining their standards, or stepping into a next-level identity. It is not a resort. It is a residency. And it is a return home to yourself.</p>
          </Reveal>
        </div>
      </section>

      <div className="cta-band">
        <p className="eyebrow" style={{ marginBottom: 24 }}>Reserve Your Place</p>
        <h2>Ready to experience Saviar?</h2>
        <p>Spaces are limited and experiences are curated. Apply to begin.</p>
        <div className="btn-group">
          <Link to="/apply" className="btn btn-primary">Apply Now</Link>
          <a href="https://calendly.com/the-soft-reset-call/discovery-call" target="_blank" rel="noopener noreferrer" className="btn btn-outline" data-testid="experiences-cta-discovery">Book a Discovery Call</a>
        </div>
      </div>
      <Footer />
    </div>
  );
}
